import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { PHOTO_FINDER_SUBSCRIPTION_BILLING_PERIOD } from '../PhotoFinderSubscriptionDetails/helpers';
import Badge from '../../../../../../../components/global/Badge';
import CustomControl from '../../../../../../../components/global/CustomControl';
import {
  formatPriceForDisplay,
  formatNumber,
} from '../../../../../../../util/format-money';
import PillButtonGroup from '../../../../../../../components/global/PillButtonGroup';

const propTypes = {
  photoFinderSubscriptionItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      max_photos: PropTypes.number,
    }).isRequired,
  ),
  onSelectionChange: PropTypes.func,
  selectable: PropTypes.bool,
  buttonWrapperClassName: PropTypes.string,
  defaultBillingPeriod: PropTypes.oneOf(
    Object.values(PHOTO_FINDER_SUBSCRIPTION_BILLING_PERIOD),
  ),
  selectableBillingPeriod: PropTypes.bool,
  defaultPhotoFinderSubscriptionItem: PropTypes.shape({
    id: PropTypes.string.isRequired,
    max_photos: PropTypes.number,
  }),
  currentSubscriptionPrice: PropTypes.number,
  pricePerPhotoHidden: PropTypes.bool,
  freeTrialHidden: PropTypes.bool,
  displayNotice: PropTypes.bool,
};

const PhotoFinderSubscriptionTable = ({
  photoFinderSubscriptionItems,
  onSelectionChange,
  selectable,
  buttonWrapperClassName,
  defaultBillingPeriod = PHOTO_FINDER_SUBSCRIPTION_BILLING_PERIOD.MONTHLY,
  selectableBillingPeriod = true,
  defaultPhotoFinderSubscriptionItem = null,
  currentSubscriptionPrice,
  pricePerPhotoHidden = false,
  freeTrialHidden = true,
  displayNotice = true,
}) => {
  const [billingPeriod, setBillingPeriod] = useState(defaultBillingPeriod);
  const [selectedSubscriptionItem, setSelectedSubscriptionItem] = useState(
    defaultPhotoFinderSubscriptionItem,
  );

  useEffect(() => {
    if (defaultBillingPeriod) {
      setBillingPeriod(defaultBillingPeriod);
    }
  }, [defaultBillingPeriod]);

  useEffect(() => {
    if (defaultPhotoFinderSubscriptionItem) {
      setSelectedSubscriptionItem(defaultPhotoFinderSubscriptionItem);
    }
  }, [defaultPhotoFinderSubscriptionItem]);

  const buttonWrapperClasses = classNames({
    'my-2': true,
    'photo-finder-pill-group': true,
    [buttonWrapperClassName]: !!buttonWrapperClassName,
  });

  return (
    <Fragment>
      {selectableBillingPeriod && (
        <PillButtonGroup className={buttonWrapperClasses}>
          <button
            onClick={event => {
              event.preventDefault();
              setBillingPeriod(
                PHOTO_FINDER_SUBSCRIPTION_BILLING_PERIOD.MONTHLY,
              );
            }}
            className={classNames({
              active:
                billingPeriod ===
                PHOTO_FINDER_SUBSCRIPTION_BILLING_PERIOD.MONTHLY,
            })}
          >
            Monthly
          </button>
          <button
            onClick={event => {
              event.preventDefault();
              setBillingPeriod(PHOTO_FINDER_SUBSCRIPTION_BILLING_PERIOD.YEARLY);
            }}
            className={classNames({
              active:
                billingPeriod ===
                PHOTO_FINDER_SUBSCRIPTION_BILLING_PERIOD.YEARLY,
            })}
          >
            Annual{' '}
            <Badge
              badgeStyle="yellow"
              badgeSize="sm"
              className="ml-1 text-extra-bold note"
            >
              Save 5%
            </Badge>
          </button>
        </PillButtonGroup>
      )}
      <div className="table-responsive horizontal-scroller">
        <table className="table table-striped mb-3">
          <thead className="text-bold text-sm">
            <tr>
              <td>&nbsp;</td>
              <td>
                {billingPeriod ===
                PHOTO_FINDER_SUBSCRIPTION_BILLING_PERIOD.MONTHLY
                  ? 'Monthly'
                  : 'Yearly'}{' '}
                Cost
              </td>
              <td>
                Photos per{' '}
                {billingPeriod ===
                PHOTO_FINDER_SUBSCRIPTION_BILLING_PERIOD.MONTHLY
                  ? 'Month'
                  : 'Year'}
              </td>
              {!pricePerPhotoHidden && <td>Est. Cost per Photo</td>}
              <td>&nbsp;</td>
            </tr>
          </thead>
          <tbody>
            {!freeTrialHidden && (
              <tr
                onClick={() => {
                  setSelectedSubscriptionItem(null);
                  onSelectionChange(null);
                }}
              >
                <td className="text-center">
                  {selectable ? (
                    <CustomControl
                      id="checkbox-free-trial"
                      type="checkbox"
                      labelSrOnly
                      label="Select plan"
                      checked={!selectedSubscriptionItem}
                      onChange={event => {
                        event.stopPropagation();
                        setSelectedSubscriptionItem(null);
                        onSelectionChange(null);
                      }}
                    />
                  ) : (
                    <>&nbsp;</>
                  )}
                </td>
                <td>Free Trial</td>
                <td>250</td>
              </tr>
            )}
            {photoFinderSubscriptionItems.map(subscriptionItem => {
              if (subscriptionItem.billing_period !== billingPeriod) {
                return null;
              }
              return (
                <tr
                  key={subscriptionItem.id}
                  className="cursor-pointer"
                  onClick={() => {
                    if (
                      currentSubscriptionPrice !== subscriptionItem.price.value
                    ) {
                      setSelectedSubscriptionItem(subscriptionItem);
                      onSelectionChange(subscriptionItem);
                    }
                  }}
                >
                  <td className="text-center">
                    {currentSubscriptionPrice ===
                    subscriptionItem.price.value ? (
                      <span className="text-extra-bold">CURRENT</span>
                    ) : (
                      <Fragment>
                        {selectable ? (
                          <CustomControl
                            id={`checkbox-${subscriptionItem.id}`}
                            type="checkbox"
                            labelSrOnly
                            label="Select plan"
                            checked={
                              selectedSubscriptionItem === subscriptionItem
                            }
                            onChange={event => {
                              event.stopPropagation();
                              setSelectedSubscriptionItem(subscriptionItem);
                              onSelectionChange(subscriptionItem);
                            }}
                          />
                        ) : (
                          <>&nbsp;</>
                        )}
                      </Fragment>
                    )}
                  </td>
                  <td>
                    $
                    {formatNumber(
                      formatPriceForDisplay(subscriptionItem.price.value, true),
                    )}
                  </td>
                  <td>{formatNumber(subscriptionItem.max_photos)}</td>
                  {!pricePerPhotoHidden && (
                    <td>
                      $
                      {formatPriceForDisplay(
                        subscriptionItem.price.value /
                          subscriptionItem.max_photos,
                        true,
                        3,
                      )}
                    </td>
                  )}
                  <td>&nbsp;</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {displayNotice && (
        <p className="text-sm text-center text-extra-bold mt-1">
          * Unused photos rollover from month to month within your contract
          year.
        </p>
      )}
    </Fragment>
  );
};

PhotoFinderSubscriptionTable.propTypes = propTypes;

export default PhotoFinderSubscriptionTable;
